// src/components/Navbar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGlobe, FaTimes, FaBars } from 'react-icons/fa';
import { useLang } from '../context/LangContext';
import { NAVBAR } from '../data/content';

const NAV_LINKS = [
  { href: '#hero',      key: 'home'      },
  { href: '#about',     key: 'about'     },
  { href: '#services',  key: 'services'  },
  { href: '#portfolio', key: 'portfolio' },
  { href: '#faq',       key: 'faq'       },
  { href: '#jobs',      key: 'jobs'      },
];

const Navbar = () => {
  const { currentLang, setCurrentLang, isRtl } = useLang();
  const [scrolled,  setScrolled ] = useState(false);
  const [menuOpen,  setMenuOpen ] = useState(false);

  const t = NAVBAR[currentLang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleLang = useCallback(() => setCurrentLang(prev => prev === 'ar' ? 'en' : 'ar'), [setCurrentLang]);
  const closeMenu  = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <nav
        className={`lsq-navbar${scrolled ? ' scrolled' : ''}`}
        dir={isRtl ? 'rtl' : 'ltr'}
        role="navigation"
        aria-label="Main Navigation"
      >
        <div className="lsq-navbar-inner">
          <a href="#hero" className="lsq-navbar-logo" onClick={closeMenu}>
            <img src="/assets/images/lawaseq-logo.png" alt="LAWASEQ" width="160" height="55" />
          </a>

          <div className="lsq-navbar-right">
            <div className="lsq-nav-links" aria-label="Desktop Navigation">
              {NAV_LINKS.map(({ href, key }) => (
                <a key={key} href={href} className="lsq-nav-link">
                  {t[key]}
                </a>
              ))}
              <a href="#contact" className="lsq-nav-cta">{t.contact}</a>
            </div>

            <button className="lsq-lang-btn" onClick={toggleLang} aria-label="Toggle Language">
              <FaGlobe size={13} />
              <span>{t.langLabel}</span>
            </button>

            <button
              className="lsq-mobile-toggle"
              onClick={() => setMenuOpen(prev => !prev)}
              aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <FaTimes size={22} color="#fff" /> : <FaBars size={22} color="#fff" />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 }}
            />

            <motion.nav
              key="drawer"
              initial={{ x: isRtl ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRtl ? '100%' : '-100%' }}
              transition={{ type: 'tween', duration: 0.35 }}
              className="lsq-mobile-menu"
              dir={isRtl ? 'rtl' : 'ltr'}
              aria-label="Mobile Navigation"
            >
              <div className="lsq-mobile-links">
                {NAV_LINKS.map(({ href, key }) => (
                  <a key={key} href={href} onClick={closeMenu}>
                    {t[key]}
                  </a>
                ))}
                <a href="#contact" className="lsq-mobile-cta" onClick={closeMenu}>
                  {t.contact}
                </a>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;